// Lets callers wait for the auth bridge (POST /api/auth/site-session) before fetching
// personalized CMS content.
//
// Visitor groups such as "Site user login state" are evaluated server-side against the
// CpgSiteUser cookie, and that cookie only exists once the bridge POST has completed. On the
// first load after an Auth0 redirect, TCSM fires the bridge and its content fetches at the same
// time; the bridge also calls WAM before it issues the cookie, so it routinely loses the race.
// The site settings (which carry the personalized site-wide alert blocks) then come back
// resolved for an anonymous visitor, and stay that way until the next full page load.
//
// Module-level rather than redux state, matching logout-state: it is one-shot per page load, the
// gate in custom-auth always starts the bridge before any gated child mounts, and a full-page
// navigation tears it down.

const DefaultWaitMs = 4000;

let resolveFirstBridge: () => void;
const firstBridge = new Promise<void>((resolve) => { resolveFirstBridge = resolve; });

/**
 * Hand the Auth0 token to the back end so it can issue the site-user cookie. Never rejects:
 * the cookie is best-effort, and a failed bridge must still release anyone waiting on it.
 */
export const establishSiteSession = (accessToken: string): Promise<void> => {
	const bridge = fetch('/api/auth/site-session', {
		method: 'POST',
		headers: { Authorization: `Bearer ${accessToken}` },
		credentials: 'same-origin',
	}).then(() => undefined, () => undefined);

	bridge.then(resolveFirstBridge);
	return bridge;
};

/**
 * Resolves once the first bridge call of this page load has completed, or after `timeoutMs`,
 * whichever comes first. The cap keeps a slow or unreachable WAM from holding up the app; past it
 * the caller proceeds and personalized content falls back to the anonymous variant, as before.
 */
export const whenSiteSessionReady = (timeoutMs: number = DefaultWaitMs): Promise<void> =>
	Promise.race([
		firstBridge,
		new Promise<void>((resolve) => { setTimeout(resolve, timeoutMs); }),
	]);
